'use client';

/**
 * The line tabs on a public place page — doc 03 §3 / doc 07 §3.
 *
 * A branch can now sell several orderable propositions at once: "Fast Food",
 * "Catering", "Bar". These are the tabs that switch between them.
 *
 * Three rules the design turns on:
 *
 *  · **One line ⇒ this renders nothing.** Chrome that can only ever show one option is
 *    noise, and it is what keeps today's 120 single-line branches pixel-identical.
 *    Enforced here as well as in `selectLine`, because a caller that forgets the check
 *    must not be able to put a one-tab strip on screen.
 *  · **A lead time is shown ON the tab.** A guest must never discover at checkout that
 *    the thing they chose needs 48 hours' notice.
 *  · **Switching tabs never touches the cart.** These are links that change what you
 *    are BROWSING; the cart is scoped to the branch (doc 02 D5), so what you are
 *    holding is untouched. That is why they are real links and not state toggles.
 *
 * The strip is a real tablist (doc 07 §7): `role="tablist"` on the list, `role="tab"`
 * with `aria-selected` on each link, a roving tabindex so exactly one tab is in the
 * tab order, and ArrowLeft/ArrowRight/Home/End selection that keeps focus on the tab
 * the guest just chose. Selection still goes through the link's href via the router,
 * so the cart and the scroll position are untouched.
 *
 * @location components/place/PlaceLineTabs.tsx
 */

import { useEffect, useRef, type KeyboardEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { cn } from '@/components/ui/utils';
import { zClass } from '@/lib/ui/z-layers';

export interface PlaceLineTab {
  id: string;
  slug: string;
  name: string;
  /** Minutes of notice this line needs. 0 = order now. */
  leadTimeMinutes: number;
  /** False once a capability pack lapses — browsable, not orderable (doc 04 §5). */
  isOrderable: boolean;
}

/** "48h", "2 days", "30 min" — the shortest true phrasing. */
function leadTimeLabel(minutes: number): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)} days`;
}

export function PlaceLineTabs({
  tabs,
  activeSlug,
  basePath,
}: {
  tabs: readonly PlaceLineTab[];
  activeSlug: string | null;
  /** The place path these tabs hang off, e.g. `/m/kwames-kitchen/spintex`. */
  basePath: string;
}) {
  const router = useRouter();

  // The slug of the tab the keyboard just selected. The router re-renders the
  // strip with that tab's `tabIndex` at 0, and the effect below restores focus
  // to it after that commit — without this, ArrowRight would move the selection
  // but leave focus on the old tab, and the next keypress would jump back.
  const pendingFocusRef = useRef<string | null>(null);

  // No dependency array: this must run after EVERY commit, and the ref guard
  // makes the no-op pass (the common case) a single null check.
  useEffect(() => {
    if (pendingFocusRef.current === null) return;
    const slug = pendingFocusRef.current;
    pendingFocusRef.current = null;
    document.getElementById(`place-line-tab-${slug}`)?.focus();
  });

  if (tabs.length < 2) return null;

  const selectedIndex = tabs.findIndex((tab) => tab.slug === activeSlug);
  // Roving tabindex: exactly one tab is in the tab order. When no line is
  // selected (no `?line=` param, or the slug no longer exists), the first tab
  // takes the slot so the strip stays keyboard-reachable.
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const selectTab = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    pendingFocusRef.current = tab.slug;
    router.push(`${basePath}?line=${encodeURIComponent(tab.slug)}`, { scroll: false });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab((focusIndex + 1) % tabs.length);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab((focusIndex - 1 + tabs.length) % tabs.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      selectTab(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      selectTab(tabs.length - 1);
    }
  };

  return (
    <nav
      // The tablist below carries the accessible name; the nav landmark needs no
      // duplicate label (screen readers would announce it twice).
      aria-label="Order lines"
      // Scrolls horizontally rather than wrapping: a branch with four lines must not
      // push the menu down the page on a phone.
      className={cn(
        'sticky top-[var(--place-header-height)] -mb-px overflow-x-auto border-b border-[var(--color-border)] bg-[var(--color-bg-base)]/95 backdrop-blur',
        zClass('sticky'),
      )}
    >
      <ul
        role="tablist"
        aria-label="What you can order"
        onKeyDown={onKeyDown}
        className="mx-auto flex w-full max-w-[var(--place-measure)] gap-1 px-3"
      >
        {tabs.map((tab, index) => {
          const active = tab.slug === activeSlug;
          const lead = leadTimeLabel(tab.leadTimeMinutes);
          return (
            <li key={tab.id} className="shrink-0">
              <Link
                id={`place-line-tab-${tab.slug}`}
                href={`${basePath}?line=${encodeURIComponent(tab.slug)}`}
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                tabIndex={index === focusIndex ? 0 : -1}
                scroll={false}
                /**
                 * The active tab points at the page the guest is already on, and
                 * this bar is sticky — so it sits in the viewport for the whole
                 * visit, prefetching a page nobody is about to navigate to.
                 *
                 * That is waste at any speed, and worse than waste when the
                 * render is slow: a prefetch that fails makes Next fall back to
                 * a full browser navigation, which the guest sees as the page
                 * reloading under them.
                 */
                prefetch={active ? false : undefined}
                className={cn(
                  'flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm transition',
                  active
                    ? 'border-[var(--color-accent)] font-semibold text-[var(--color-text-primary)]'
                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                )}
              >
                <span className="whitespace-nowrap text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]">{tab.name}</span>
                {lead ? (
                  // On the tab, not at checkout. Discovering a 48-hour lead time after
                  // building a basket wastes the guest's evening.
                  <span className="rounded-full bg-[var(--color-bg-raised)] px-1.5 py-0.5 text-[0.6875rem] font-medium text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)]">
                    {lead}
                  </span>
                ) : null}
                {!tab.isOrderable ? (
                  <span className="text-[0.6875rem] text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)]">
                    view only
                  </span>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}